'use client';

import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAssignments, useUpdateAssignments } from '@/features/playbooks/api/use-assignments';
import { useActivation } from '@/features/playbooks/api/use-playbook-activations';
import { WorkflowSectionTabs } from '@/features/playbooks/components/WorkflowSectionTabs';
import { AssigneeSelect } from '@/features/task-console/components/AssigneeSelect';
import { useReportingSettings } from '@/features/datahub/periods/api/use-reporting-settings';
import { useActiveOrganizationId } from '@/features/tasks/hooks/use-active-organization-id';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

const CADENCE_LABELS: Record<string, string> = {
  once: 'once',
  monthly: 'monthly',
  quarterly: 'quarterly',
  annually: 'annually',
  annual: 'annually',
};

function useResolveCadenceLabel(cadence: string | null | undefined): string {
  const { data: settings } = useReportingSettings();
  const raw = cadence ?? 'once';
  if (raw === 'reporting_period') {
    const granularity = settings?.reportingGranularity ?? 'monthly';
    return CADENCE_LABELS[granularity] ?? 'monthly';
  }
  return CADENCE_LABELS[raw] ?? raw;
}

export const Route = createFileRoute('/_authenticated/tasks/playbooks/$activationId/setup')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.workflowAssignments'),
  }),
  component: PlaybookSetupPage,
});

/** Inline component to resolve cadence label (hooks can't be called in .map()) */
function ResolvedCadenceLabel({ cadence }: { cadence: string | null | undefined }) {
  const label = useResolveCadenceLabel(cadence);
  return <>{label}</>;
}

function PlaybookSetupPage() {
  const { t } = useTranslation('tasks');
  const { activationId } = Route.useParams();
  const { activation, isLoading } = useActivation(activationId);
  const { assignments, isLoading: assignmentsLoading } = useAssignments(activationId);
  const { updateAssignments, isUpdating } = useUpdateAssignments(activationId);
  const { organizationId } = useActiveOrganizationId();

  // Track pending assignee changes: assignmentId -> assigneeId (null = clear)
  const [pendingChanges, setPendingChanges] = React.useState<
    Record<string, string | null>
  >({});

  const hasPendingChanges = Object.keys(pendingChanges).length > 0;

  const handleAssigneeChange = (assignmentId: string, value: string | string[] | null) => {
    const assigneeId = Array.isArray(value) ? (value[0] ?? null) : value;
    setPendingChanges((prev) => {
      // Find current assignment to check if this is reverting to original
      const assignment = assignments.find((a) => a.id === assignmentId);
      const originalAssigneeId = assignment?.assigneeId ?? null;
      if (assigneeId === originalAssigneeId) {
        // Remove from pending changes (reverted to original)
        return Object.fromEntries(
          Object.entries(prev).filter(([key]) => key !== assignmentId)
        );
      }
      return { ...prev, [assignmentId]: assigneeId };
    });
  };

  const handleSave = async () => {
    if (!hasPendingChanges) return;

    const updates = Object.entries(pendingChanges).map(([assignmentId, assigneeId]) => ({
      assignmentId,
      assigneeId,
    }));

    try {
      await updateAssignments(updates);
      setPendingChanges({});
      toast.success(t('tasks:toast.assignmentsUpdatedSuccessfully'));
    } catch {
      toast.error(t('tasks:toast.failedToUpdateAssignments'));
    }
  };

  if (isLoading || assignmentsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!activation) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">{t('setup.notFound')}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <WorkflowSectionTabs activeTab="runs" />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/tasks/playbooks/$activationId" params={{ activationId }}>
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('setup.title', { name: activation.name })}</h1>
            <p className="text-sm text-muted-foreground">
              {t('setup.description')}
            </p>
          </div>
        </div>
        {hasPendingChanges && (
          <Button onClick={() => void handleSave()} disabled={isUpdating}>
            {isUpdating ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            {t('setup.saveChanges')}
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('setup.obligationAssignments')}</CardTitle>
          <CardDescription>
            {t('setup.assignDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {assignments.map((assignment) => {
              const currentAssigneeId: string | null =
                assignment.id in pendingChanges
                  ? (pendingChanges[assignment.id] ?? null)
                  : (assignment.assigneeId ?? null);

              return (
                <div
                  key={assignment.id}
                  className="flex items-center justify-between rounded-lg border p-3"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{assignment.obligationName}</p>
                    <p className="text-xs text-muted-foreground">
                      <ResolvedCadenceLabel cadence={assignment.cadence} /> &middot; {t('setup.priority', { priority: assignment.priority ?? 'medium' })}
                      {assignment.assigneeRole && (
                        <> &middot; {t('setup.role', { role: assignment.assigneeRole })}</>
                      )}
                    </p>
                  </div>
                  <div className="ml-4 w-[220px]">
                    {organizationId ? (
                      <AssigneeSelect
                        orgId={organizationId}
                        value={currentAssigneeId}
                        onChange={(value) => { handleAssigneeChange(assignment.id, value); }}
                        multiple={false}
                        placeholder={t('setup.unassigned')}
                      />
                    ) : (
                      <span className="text-sm text-muted-foreground">
                        {assignment.assignee
                          ? assignment.assignee.displayName
                          : assignment.assigneeRole ?? t('setup.unassigned')}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
